/**
 * @prop --content-background: Content background
 *
 * @prop --stzh-accordion-content-background: **Global**: Content background
 * @prop --stzh-accordion-button-color: **Global**: Button label background
 */

:host {
  --trigger-size: 18px;
  --trigger-color: currentColor;
  --content-background: #{$accordionContentBackground};
  --icon-size: #{iconSize()};
  --button-color: var(--stzh-accordion-button-color, #{$colorPrimary70});
  --button-background-color: var(
    --stzh-accordion-button-background-color,
    #{$colorSecondary30}
  );
  --button-background-color-is-open: var(
    --stzh-accordion-button-background-color-is-open,
    #{$colorSecondary30}
  );

  --hover-button-color: var(--button-color);
  --hover-button-background-color: var(
    --stzh-accordion-button-hover-background-color,
    #{$colorSecondary40}
  );
  --hover-trigger-color: var(--trigger-color);

  --transition-duration: 200ms;

  @media print {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  @include highContrast() {
    --trigger-color: CanvasText;

    --hover-trigger-color: CanvasText;
  }
}

/* Table */
:host(:where([is-variant="table"])) {
  --trigger-size: #{iconSize()};
  --content-background: #{$colorGrey10};
  --button-background-color: #{$colorGrey20};
  --trigger-color: #{$colorBlack};

  --hover-button-background-color: #{$colorGrey30};

  @include highContrast() {
    --trigger-color: CanvasText;

    --hover-trigger-color: CanvasText;
  }
}

/* Disabled */

:host(:where([disabled]:not([disabled="false"]))) {
  --button-color: #{$colorGrey80};
  --button-background-color: #{$colorGrey20};
  --trigger-color: #{$colorGrey80};

  --hover-button-color: var(--button-color);
  --hover-button-background-color: var(--button-background-color);
  --hover-trigger-color: var(--trigger-color);
}

.stzh-accordion-item {
  &__heading {
    position: relative;
    margin: 0;
  }

  &__button {
    @include fontCurve("p1");
    @include spaceCurve("padding-top", "regular");
    @include spaceCurve("padding-bottom", "regular");
    font-family: inherit;
    width: 100%;
    appearance: none;
    display: flex;
    align-items: center;
    min-height: 60px;
    color: var(--button-color);
    background-color: var(--button-background-color);
    text-align: left;
    border: none;
    transition-property: background-color;
    transition-duration: var(--transition-duration);
    cursor: pointer;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    &:hover {
      color: var(--hover-button-color);
      background-color: var(--hover-button-background-color);
    }
  }

  &__button,
  &__content-inner {
    @include spaceCurve("padding-left", "regular");
    @include spaceCurve("padding-right", "regular");
  }

  &__button-text {
    flex-grow: 1;
  }

  &__button-icon {
    &.is-default {
      position: relative;
      display: block;
      width: var(--trigger-size);
      height: var(--trigger-size);
      margin-left: space("medium");

      &::before,
      &::after {
        position: absolute;
        top: 50%;
        left: 50%;
        content: "";
        display: block;
        width: var(--trigger-size);
        height: 2.5px;
        transition-property: transform, background-color;
        transition-duration: var(--transition-duration);
        transform-origin: top left;
        background-color: var(--trigger-color);

        @media (prefers-reduced-motion: reduce) {
          transition: none;
        }
      }

      &::before {
        transform: rotate(180deg) translate(-50%, -50%);
      }

      &::after {
        transform: rotate(90deg) translate(-50%, -50%);
      }
    }

    &.is-table {
      --size: var(--trigger-size);

      transition-property: transform, color;
      transition-duration: var(--transition-duration);
      color: var(--trigger-color);

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    }
  }

  &__button:hover &__button-icon {
    &.is-default {
      &::before,
      &::after {
        background-color: var(--hover-trigger-color);
      }
    }

    &.is-table {
      color: var(--hover-trigger-color);
    }
  }

  &__content {
    overflow: hidden;
    background-color: var(--content-background);

    @media print {
      height: auto !important;
      overflow: visible !important;
      display: block !important;
    }
  }

  &__content-inner {
    @include spaceCurve("padding-top", "medium");
    @include spaceCurve("padding-bottom", "medium");
  }

  &__icon-wrapper {
    --stzh-icon-size: var(--icon-size);

    align-self: flex-start;
  }

  &__badge {
    position: absolute;
    right: -5px;
    top: -5px;
  }

  &__description {
    @include fontCurve("caption");
  }

  &__heading-text,
  &__description,
  &__subtitle {
    display: block;
  }

  &__subtitle {
    @include fontCurve("p2");
  }

  /* Has icon */

  &--has-icon &__icon-wrapper {
    margin-right: space("xsmall");
  }

  /* Title Variant / Subtitle */

  &--title &__heading-text,
  &--has-subtitle &__heading-text {
    @include font("title");
    @include fontCurve("h3", "heading");
  }

  &--title &__subtitle:not(:empty) + &__heading-text,
  &--has-subtitle &__subtitle:not(:empty) + &__heading-text {
    @include spaceCurve("margin-top", "tiny");

    @include mq($from: medium) {
      margin-top: space("xsmall");
    }
  }

  /* Open */

  &--is-open &__button {
    background-color: var(--button-background-color-is-open);
  }

  &--is-open &__button-icon {
    &.is-default {
      &::before,
      &::after {
        transform: rotate(0) translate(-50%, -50%);
      }
    }

    &.is-table {
      transform: rotate(180deg);
    }
  }

  /* Table Variant */

  &--table &__button,
  &--table &__content-inner {
    padding-left: space("medium");
    padding-right: space("medium");
  }

  &__button {
    @include fontSize("milli");
    padding-top: space("medium");
    padding-bottom: space("medium");
    min-height: 56px;
  }

  &--table &__content-inner {
    @include spaceCurve("padding-top", "tiny");
    @include spaceCurve("padding-bottom", "regular");
  }

  &--table &__heading-text {
    @include font("heavy");
  }

  /* Disabled */

  &--is-disabled &__button {
    cursor: not-allowed;
  }
}
